<template>
	<div class="pd-search" role="dialog" aria-modal="true" :aria-label="labels.placeholder" @click.self="$emit('close')">
		<div class="pd-search__panel">
			<label class="pd-search__field">
				<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="2" /><path d="m20 20-3.5-3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
				<span class="pd-visually-hidden">{{ labels.placeholder }}</span>
				<input ref="input" v-model="query" type="search" :placeholder="labels.placeholder" autocomplete="off" @keydown="onKey" />
				<kbd>Esc</kbd>
			</label>
			<ul v-if="results.length" class="pd-search__results" role="listbox">
				<li v-for="(page, index) in results" :key="page.route" role="option" :aria-selected="index === cursor">
					<a :href="page.route" :class="{ 'is-active': index === cursor }" @mousemove="cursor = index">
						<strong>{{ page.title }}</strong><small>{{ page.section }}</small>
					</a>
				</li>
			</ul>
			<p v-else class="pd-search__empty">{{ labels.empty }}</p>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";

const props = defineProps({ pages: { type: Array, required: true }, labels: { type: Object, required: true } });
const emit = defineEmits(["close"]);
const query = ref("");
const cursor = ref(0);
const input = ref(null);

// Every word of the query must appear in the title or the section; title matches rank first.
const results = computed(() => {
	const words = query.value.toLowerCase().split(/\s+/).filter(Boolean);
	if (!words.length) return props.pages.slice(0, 12);
	return props.pages
		.map((page) => {
			const title = page.title.toLowerCase();
			const haystack = `${title} ${page.section.toLowerCase()}`;
			if (!words.every((w) => haystack.includes(w))) return null;
			const score = words.reduce((sum, w) => sum + (title.startsWith(w) ? 3 : title.includes(w) ? 2 : 1), 0);
			return { page, score };
		})
		.filter(Boolean)
		.sort((a, b) => b.score - a.score)
		.slice(0, 30)
		.map((r) => r.page);
});

watch(query, () => (cursor.value = 0));
onMounted(() => input.value && input.value.focus());

function onKey(event) {
	if (event.key === "Escape") emit("close");
	else if (event.key === "ArrowDown") {
		event.preventDefault();
		cursor.value = Math.min(results.value.length - 1, cursor.value + 1);
	} else if (event.key === "ArrowUp") {
		event.preventDefault();
		cursor.value = Math.max(0, cursor.value - 1);
	} else if (event.key === "Enter" && results.value[cursor.value]) {
		location.href = results.value[cursor.value].route;
	}
}
</script>
